import React from 'react'

import { KpiVariable, KpiVariableWithChildren } from 'types/GlobalKpiVariable'
import { translateOperator } from './dialogs/KpiFormulaBlock/utils'

import { TMetricCalculationBlock } from 'types/GlobalMetric'
import { ICustomEconomyMetric } from 'types/GlobalMetric'
import Chip from 'components_new/atoms/Chip'

function sideToNode(
  blockSide:
    | {
        value?: number | string | null
        kpi_variable?: { id: string } | null
        kpi_template?: { id: string } | null
        kpi_calculation_block?: TMetricCalculationBlock | null
      }
    | null
    | undefined,
  variables: KpiVariable[],
  templates: ICustomEconomyMetric[]
): React.ReactNode {
  if (!blockSide) return null

  // recursive side -> render block (not a leaf)
  if (blockSide.kpi_calculation_block) {
    return (
      <span>
        ({blockToNode(blockSide.kpi_calculation_block, variables, templates)})
      </span>
    )
  }

  // leaf side -> figure out label, wrap in Chip
  let label = ''

  if (blockSide.value != null) {
    label = blockSide.value.toString()
  }

  if (blockSide.kpi_variable) {
    const v = variables.find((x) => x.id === blockSide.kpi_variable?.id)

    label = v?.name ?? ''
  }

  if (blockSide.kpi_template) {
    const t = templates.find((x) => x.id === blockSide.kpi_template?.id)

    label = t?.title.sv ?? ''
  }

  return <Chip label={label} size="xsmall" sx={{ verticalAlign: 'middle' }} />
}

function blockToNode(
  block: TMetricCalculationBlock,
  variables: KpiVariable[],
  templates: ICustomEconomyMetric[]
): React.ReactNode {
  const leftNode = sideToNode(
    {
      value: block.left_value,
      kpi_variable: block.left_kpi_variable_id
        ? { id: block.left_kpi_variable_id }
        : null,
      kpi_template: block.left_kpi_template_id
        ? { id: block.left_kpi_template_id }
        : null,
      kpi_calculation_block: block.left_kpi_calculation_block
    },
    variables,
    templates
  )

  const rightNode = sideToNode(
    {
      value: block.right_value,
      kpi_variable: block.right_kpi_variable_id
        ? { id: block.right_kpi_variable_id }
        : null,
      kpi_template: block.right_kpi_template_id
        ? { id: block.right_kpi_template_id }
        : null,
      kpi_calculation_block: block.right_kpi_calculation_block
    },
    variables,
    templates
  )

  const op = block.operation ? translateOperator[block.operation] : ''

  return (
    <>
      {leftNode}
      {op && (
        <>
          {' '}
          <span>{op}</span>{' '}
        </>
      )}
      {rightNode}
    </>
  )
}

export function getKpiFormulaNode(
  formula: TMetricCalculationBlock | null,
  variables: KpiVariable[],
  templates: ICustomEconomyMetric[]
): React.ReactNode | null {
  if (!formula) return null

  return blockToNode(formula, variables, templates)
}

function blockToString(
  block: TMetricCalculationBlock,
  variables: KpiVariable[],
  templates: ICustomEconomyMetric[]
): string {
  let left = ''
  let right = ''

  if (block.left_value) {
    left = block.left_value.toString()
  }
  if (block.right_value) {
    right = block.right_value.toString()
  }

  if (block.left_kpi_variable_id) {
    const variable = variables.find(
      (v) => v.id === block.left_kpi_variable_id
    ) as KpiVariable

    left = variable.name
  }
  if (block.right_kpi_variable_id) {
    const variable = variables.find(
      (v) => v.id === block.right_kpi_variable_id
    ) as KpiVariable

    right = variable.name
  }
  if (block.left_kpi_template_id) {
    const template = templates.find(
      (v) => v.id === block.left_kpi_template_id
    ) as ICustomEconomyMetric

    left = template.title.sv
  }
  if (block.right_kpi_template_id) {
    const template = templates.find(
      (v) => v.id === block.right_kpi_template_id
    ) as ICustomEconomyMetric

    right = template.title.sv
  }

  if (block.left_kpi_calculation_block) {
    left = `(${blockToString(
      block.left_kpi_calculation_block,
      variables,
      templates
    )})`
  }
  if (block.right_kpi_calculation_block) {
    right = `(${blockToString(
      block.right_kpi_calculation_block,
      variables,
      templates
    )})`
  }

  return `${left} ${
    block.operation ? translateOperator[block.operation] : ''
  } ${right}`
}

export function getKpiFormulaString(
  formula: TMetricCalculationBlock | null,
  variables: KpiVariable[],
  templates: ICustomEconomyMetric[]
): string | null {
  if (formula) {
    return blockToString(formula, variables, templates)
  }

  return null
}

export function parseKpiVariableWithChildren(
  variables: KpiVariable[]
): KpiVariableWithChildren[] {
  const levelZero = variables.filter((v) => v.level === 0)

  const levelOne = variables.filter((v) => v.level === 1)

  const levelTwo = variables.filter((v) => v.level === 2)

  return levelZero.map((variable) => ({
    ...variable,
    children: levelOne
      .filter((v) => v.parent_kpi_variable_id === variable.id)
      .map((variableOne) => ({
        ...variableOne,
        children: levelTwo
          .filter((v) => v.parent_kpi_variable_id === variableOne.id)
          .map((v) => ({
            ...v,
            children: []
          }))
      }))
  }))
}
